import { Children, forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import {
  badgeVariants,
  type BadgeSize,
  type BadgeVariant,
} from "@/components/ui/badge-variants";
import { cn } from "@/lib/utils";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
}

function compactBadgeChildren(children: ReactNode): ReactNode[] {
  return Children.toArray(children).filter(
    (child) => typeof child !== "string" || child.trim() !== "",
  );
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, dot, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {dot && (
          <span
            className={cn(
              "size-[0.45em] shrink-0 rounded-full",
              variant === "success" && "bg-success-500",
              variant === "warning" && "bg-warning-500",
              variant === "error" && "bg-error-500",
              variant === "primary" && "bg-primary-500",
              variant === "accent" && "bg-accent-500",
              (!variant || variant === "default" || variant === "outline") &&
                "bg-surface-500",
            )}
          />
        )}
        {compactBadgeChildren(children)}
      </span>
    );
  },
);

Badge.displayName = "Badge";

export { Badge };
